import { Linkedin } from "lucide-react";
import { Reveal, MaskIn, MaskLine, SectionHead, ParallaxImg } from "@/components/motion";

const VALUES = [
    { t: "Expertise", d: "Research-informed and professionally grounded advice." },
    { t: "Clarity", d: "Making complex issues understandable and actionable." },
    { t: "Integrity", d: "Independent thinking and responsible professional practice." },
    { t: "Practicality", d: "Recommendations designed for implementation, not simply presentation." },
    { t: "Performance", d: "A clear focus on outcomes, organisational improvement and long-term value." },
];

const AUDIENCE = [
    { t: "Businesses and SMEs", d: "seeking sustainable growth and stronger organisational structures." },
    { t: "Corporate organisations", d: "navigating governance, ESG, risk and strategic transformation." },
    { t: "Public and third-sector organisations", d: "requiring research, governance, policy or organisational advisory." },
    { t: "Executives and boards", d: "seeking strategic insight and independent advisory support." },
    { t: "International organisations and institutions", d: "addressing sustainability, governance and regulatory challenges." },
];

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85";

const LEADER_IMG = "/dr-shanu-alabi.jpg";

export default function About() {
    return (
        <div>
            <section
                className="mx-auto max-w-7xl px-6 pt-32 md:px-12 md:pt-44"
                data-testid="about-hero"
            >
                <Reveal y={16}>
                    <p className="eyebrow text-ink/50">About ELCAYIL</p>
                </Reveal>
                <h1 className="mt-8 max-w-5xl font-serif-d text-4xl leading-[1.1] sm:text-5xl md:text-7xl">
                    <MaskLine delay={0.2}>
                        Strong organisations are built
                    </MaskLine>
                    <MaskLine delay={0.32}>
                        through <em className="italic text-brand">clarity</em>,
                    </MaskLine>
                    <MaskLine delay={0.44}>
                        governance and disciplined execution.
                    </MaskLine>
                </h1>
                <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <Reveal className="lg:col-span-6">
                        <p className="text-[15px] leading-relaxed text-ink/60">
                            At ELCAYIL CONSULTING LTD., we believe strong
                            organisations are built through clear strategy,
                            responsible leadership, sound governance and
                            disciplined execution. We work with businesses,
                            institutions, leaders and organisations to identify
                            challenges, strengthen strategic and operational
                            capabilities, manage emerging risks and develop
                            practical pathways toward sustainable growth.
                        </p>
                        <p className="mt-6 text-[15px] leading-relaxed text-ink/60">
                            Our approach combines research, professional
                            expertise, strategic thinking and real-world
                            implementation. We do not simply identify problems.
                            We help organisations move from insight to action —
                            and from action to performance.
                        </p>
                    </Reveal>
                    <Reveal
                        delay={0.15}
                        className="group lg:col-span-5 lg:col-start-8"
                    >
                        <ParallaxImg
                            src={ABOUT_IMG}
                            alt="Boardroom overlooking the city"
                            className="aspect-[4/3] w-full"
                        />
                        <p className="eyebrow mt-3 text-ink/40">
                            Advisory built on evidence and implementation
                        </p>
                    </Reveal>
                </div>
            </section>

            <section
                className="mt-24 border-t border-black/10 md:mt-32"
                data-testid="about-leadership"
            >
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="01" label="Leadership" />
                    <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-5">
                            <MaskIn>
                                <h2 className="font-serif-d text-4xl md:text-5xl">
                                    Dr. Shanu Alabi, PhD
                                </h2>
                            </MaskIn>
                            <Reveal delay={0.15}>
                                <p className="eyebrow mt-4 text-brand">
                                    Founder & Lead Advisor
                                </p>
                                <p className="mt-8 text-[15px] leading-relaxed text-ink/60">
                                    ELCAYIL CONSULTING LTD. is led by Dr. Shanu
                                    Alabi, whose work spans sustainable
                                    management, ESG, regulatory compliance,
                                    governance, organisational performance and
                                    the transition toward more responsible and
                                    sustainable business practices.
                                </p>
                                <p className="mt-6 text-[15px] leading-relaxed text-ink/60">
                                    Her approach combines academic insight with
                                    practical business thinking, enabling
                                    organisations to better understand complex
                                    sustainability and governance challenges and
                                    translate them into actionable strategies.
                                    Her areas of professional and research
                                    interest include sustainable management, ESG
                                    regulation, compliance, governance,
                                    sustainability strategy and low-carbon
                                    transition finance.
                                </p>
                                <p className="mt-6 text-[15px] leading-relaxed text-ink/60">
                                    Through ELCAYIL CONSULTING LTD., this
                                    expertise is translated into practical
                                    advisory services for organisations seeking
                                    stronger governance, responsible growth,
                                    regulatory preparedness and improved
                                    organisational performance.
                                </p>
                                <a
                                    href="https://www.linkedin.com/in/shanualabi/"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="about-linkedin-link"
                                    className="link-line mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand"
                                >
                                    <Linkedin className="h-4 w-4" />
                                    Connect with Dr. Shanu Alabi on LinkedIn
                                </a>
                            </Reveal>
                        </div>
                        <Reveal
                            delay={0.2}
                            className="group lg:col-span-6 lg:col-start-7"
                        >
                            <div className="overflow-hidden">
                                <img
                                    src={LEADER_IMG}
                                    alt="Dr. Shanu Alabi, Founder and Lead Advisor of ELCAYIL CONSULTING LTD."
                                    className="spotlight-img aspect-square w-full object-cover"
                                />
                            </div>
                            <p className="eyebrow mt-3 text-center text-ink/40">
                                Dr. Shanu Alabi, PhD — Founder & Lead Advisor
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section
                className="border-t border-black/10"
                data-testid="about-values"
            >
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="02" label="What we stand for" />
                    <MaskIn className="mt-12 max-w-3xl">
                        <h2 className="font-serif-d text-4xl md:text-6xl">
                            Five commitments behind every engagement.
                        </h2>
                    </MaskIn>
                    <div className="mt-14">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.t} delay={i * 0.05} y={20}>
                                <div
                                    className="group grid grid-cols-12 items-baseline gap-4 border-t border-black/10 py-6 md:py-8"
                                    data-testid={`about-value-row-${i + 1}`}
                                >
                                    <span className="col-span-2 font-mono2 text-xs text-brand md:col-span-1">
                                        0{i + 1}
                                    </span>
                                    <span className="row-bg col-span-10 font-serif-d text-2xl transition-colors duration-300 group-hover:text-brand md:col-span-4 md:text-3xl">
                                        {v.t}
                                    </span>
                                    <span className="col-span-10 col-start-3 text-sm leading-relaxed text-ink/60 md:col-span-6 md:col-start-6 md:text-[15px]">
                                        {v.d}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                        <div className="border-t border-black/10" />
                    </div>
                </div>
            </section>

            <section
                className="border-t border-black/10"
                data-testid="about-audience"
            >
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="03" label="Who we serve" />
                    <MaskIn className="mt-12 max-w-3xl">
                        <h2 className="font-serif-d text-4xl md:text-6xl">
                            Leaders and organisations
                            <br />
                            <em className="italic text-ink/50">
                                with serious questions.
                            </em>
                        </h2>
                    </MaskIn>
                    <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-black/10 bg-black/10 md:grid-cols-2 lg:grid-cols-3">
                        {AUDIENCE.map((a, i) => (
                            <Reveal key={a.t} delay={i * 0.06} y={20}>
                                <div
                                    className="flex h-full flex-col bg-white p-8 transition-colors duration-300 hover:bg-paper md:p-10"
                                    data-testid={`about-audience-card-${i + 1}`}
                                >
                                    <span className="font-mono2 text-xs text-brand">
                                        0{i + 1}
                                    </span>
                                    <p className="mt-6 font-serif-d text-xl leading-snug md:text-2xl">
                                        {a.t}
                                    </p>
                                    <p className="mt-4 text-sm leading-relaxed text-ink/60">
                                        {a.d}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal delay={0.3} y={20}>
                            <div className="flex h-full flex-col justify-between bg-brand p-8 text-white md:p-10">
                                <p className="eyebrow text-white/50">
                                    Excellent in Performance.
                                </p>
                                <p className="mt-8 font-serif-d text-xl italic leading-snug md:text-2xl">
                                    Independent thinking, responsibly practised.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
